import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import QRCode from "react-qr-code";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import {
  RefreshCcw,
  FileText,
  Package,
  ShoppingCart,
  AlertCircle,
  Settings2,
  Loader2,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Copy,
  QrCode,
} from "lucide-react";
import { Link } from "react-router-dom";

function formatMoney(v, currency = "TRY") {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    return `${n.toFixed(2)} ${currency}`;
  }
}

function CartRow({ cart, isSelected, onToggle }) {
  const [open, setOpen] = useState(false);
  const items = cart._items || [];
  const currency = items[0]?.currency || cart.currency || "TRY";
  return (
    <>
      <tr
        className={[
          "row-hover border-b border-zinc-200 transition-colors duration-150",
          isSelected ? "bg-[#002FA7]/[0.04]" : "bg-white",
        ].join(" ")}
        data-testid={`cart-row-${cart._cart_id}`}
      >
        <td className="px-3 py-3 w-10 align-top">
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => onToggle(cart._cart_id)}
            className="rounded-sm border-zinc-400 data-[state=checked]:bg-[#002FA7] data-[state=checked]:border-[#002FA7]"
            data-testid={`cart-checkbox-${cart._cart_id}`}
          />
        </td>
        <td className="px-3 py-3 align-top">
          <div className="font-mono text-xs text-zinc-500">#{cart._cart_id}</div>
          {cart._customer_name ? (
            <div
              className="mt-1 text-[13px] font-medium text-zinc-900 truncate max-w-[180px]"
              title={cart._customer_name}
              data-testid={`cart-customer-name-${cart._cart_id}`}
            >
              {cart._customer_name}
            </div>
          ) : (
            <div className="mt-1 text-[11px] text-zinc-400 italic">Misafir</div>
          )}
          <button
            type="button"
            className="mt-1 text-[11px] font-mono uppercase tracking-widest text-[#002FA7] hover:underline"
            onClick={() => setOpen(true)}
            data-testid={`cart-details-btn-${cart._cart_id}`}
          >
            Detayları gör
          </button>
        </td>
        <td className="px-3 py-3 align-top">
          <div className="flex items-center gap-2 flex-wrap max-w-[420px]">
            <Badge
              variant="secondary"
              className="rounded-sm bg-zinc-100 text-zinc-800 border border-zinc-200 font-mono text-[10px] uppercase tracking-wider"
            >
              <Package className="h-3 w-3 mr-1" />
              {cart._item_count} kalem
            </Badge>
            {items.slice(0, 2).map((it, idx) => (
              <span
                key={idx}
                className="text-xs text-zinc-700 truncate max-w-[180px]"
                title={it.name}
              >
                {it.name}
              </span>
            ))}
            {items.length > 2 ? (
              <span className="text-[11px] text-zinc-400 font-mono">
                +{items.length - 2}
              </span>
            ) : null}
          </div>
        </td>
        <td className="px-3 py-3 text-right align-top font-mono text-sm tabular-nums">
          {formatMoney(cart._subtotal, currency)}
        </td>
      </tr>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm" data-testid={`cart-details-dialog-${cart._cart_id}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-[#002FA7]" />
              Sepet #{cart._cart_id}
              {cart._customer_name ? (
                <span className="text-sm font-normal text-zinc-600">
                  · {cart._customer_name}
                </span>
              ) : null}
            </DialogTitle>
            <DialogDescription className="font-mono text-[11px] uppercase tracking-widest">
              E-posta, telefon, adres gizlendi · Taslak siparişte müşteri bilgisi kullanılmaz
            </DialogDescription>
          </DialogHeader>
          <div className="border border-zinc-200 divide-y divide-zinc-200 max-h-[420px] overflow-y-auto">
            {items.map((it, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 px-3 py-2"
                data-testid={`cart-item-${cart._cart_id}-${idx}`}
              >
                {it.image ? (
                  <img
                    src={it.image}
                    alt=""
                    className="h-12 w-12 rounded-sm border border-zinc-200 object-cover bg-white"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-sm border border-zinc-200 bg-zinc-50 flex items-center justify-center text-zinc-400">
                    <Package className="h-4 w-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-zinc-950 truncate">
                    {it.name || "—"}
                  </div>
                  <div className="text-[11px] font-mono text-zinc-500 truncate">
                    SKU: {it.sku || "—"}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                    {it.quantity} × {formatMoney(it.price, it.currency)}
                  </div>
                  <div className="text-sm font-mono tabular-nums">
                    {formatMoney((Number(it.price) || 0) * (Number(it.quantity) || 0), it.currency)}
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-zinc-500">
                Ürün bulunamadı.
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                Ara toplam
              </div>
              <div className="text-lg font-black font-mono tabular-nums">
                {formatMoney(cart._subtotal, currency)}
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CartsPage() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorNeedsSettings, setErrorNeedsSettings] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [qrOpen, setQrOpen] = useState({}); // {cart_id: bool}

  async function load() {
    setLoading(true);
    setError("");
    setErrorNeedsSettings(false);
    try {
      const { data } = await api.get("/carts/active");
      if (data.ok === false) {
        // Backend now returns 200 with {ok:false, error, status_code} to bypass CF 5xx interception.
        const msg = data.error || "Sepetler yüklenemedi";
        setError(msg);
        if (msg.includes("API ayarları")) setErrorNeedsSettings(true);
        setCarts([]);
      } else {
        setCarts(data.carts || []);
      }
      setSelected(new Set());
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      if (msg.includes("API ayarları")) setErrorNeedsSettings(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const allChecked = useMemo(
    () => carts.length > 0 && selected.size === carts.length,
    [carts, selected],
  );

  const toggleAll = () => {
    if (allChecked) {
      setSelected(new Set());
    } else {
      setSelected(new Set(carts.map((c) => c._cart_id)));
    }
  };

  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };

  const doCreate = async () => {
    setCreating(true);
    try {
      const ids = Array.from(selected);
      const { data } = await api.post("/drafts/create", { cart_ids: ids });
      if (data.ok === false) {
        toast.error("İşlem başarısız", { description: data.error });
        setCreating(false);
        return;
      }
      const okCount = data.success || 0;
      const failCount = (data.total || 0) - okCount;
      if (okCount > 0) {
        toast.success(`${okCount} taslak sipariş oluşturuldu`, {
          description: failCount ? `${failCount} sepet için hata alındı.` : "Bağlantıları aşağıda listeledik.",
        });
      }
      if (failCount > 0 && okCount === 0) {
        toast.error(`${failCount} sepet için hata`, {
          description: data.results
            .filter((r) => !r.ok)
            .slice(0, 3)
            .map((r) => `#${r.cart_id}: ${r.error}`)
            .join(" · "),
        });
      }
      // Swap the confirm dialog for a results dialog that lists draft links.
      setResults(data.results || []);
      setConfirmOpen(false);
      setResultOpen(true);
      setQrOpen({});
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error("İşlem başarısız", { description: formatApiError(e) });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <PageHeader
        subtitle="Canlı veri · IdeaSoft Admin API"
        title="Aktif Sepetler"
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="rounded-sm border-zinc-300 h-9"
              data-testid="refresh-carts-btn"
            >
              <RefreshCcw className="h-4 w-4 mr-1.5" />
              Yenile
            </Button>
          </>
        }
      />

      <div className="max-w-[1400px] mx-auto px-6 py-8 pb-32">
        {error ? (
          <div
            className="rounded-sm border border-red-200 bg-red-50 p-4 flex items-start gap-3 mb-6"
            data-testid="carts-error"
          >
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm font-semibold text-red-900">
                Sepetler çekilemedi
              </div>
              <div className="text-xs text-red-700 mt-1">{error}</div>
              {errorNeedsSettings ? (
                <Link
                  to="/settings"
                  className="mt-2 inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline font-semibold"
                >
                  <Settings2 className="h-3.5 w-3.5" /> API Ayarlarına git
                </Link>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="border border-zinc-200 bg-white">
          <div className="border-b border-zinc-200 bg-zinc-50 px-3 py-2 flex items-center gap-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {loading ? "Yükleniyor…" : `${carts.length} sepet · ${selected.size} seçili`}
            </div>
          </div>

          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="px-3 py-2 w-10">
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={toggleAll}
                    disabled={loading || carts.length === 0}
                    className="rounded-sm border-zinc-400 data-[state=checked]:bg-[#002FA7] data-[state=checked]:border-[#002FA7]"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Sepet · Müşteri
                </th>
                <th className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Ürünler
                </th>
                <th className="px-3 py-2 text-right text-[10px] font-mono uppercase tracking-widest text-zinc-500 font-semibold">
                  Ara toplam
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-zinc-200">
                    <td className="px-3 py-3">
                      <Skeleton className="h-4 w-4 rounded-sm" />
                    </td>
                    <td className="px-3 py-3">
                      <Skeleton className="h-4 w-20 rounded-sm" />
                    </td>
                    <td className="px-3 py-3">
                      <Skeleton className="h-4 w-72 rounded-sm" />
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Skeleton className="h-4 w-20 rounded-sm ml-auto" />
                    </td>
                  </tr>
                ))
              ) : carts.length === 0 && !error ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-3 py-16 text-center"
                    data-testid="carts-empty"
                  >
                    <div className="mx-auto max-w-sm">
                      <div className="mx-auto h-12 w-12 border border-zinc-300 flex items-center justify-center rounded-sm">
                        <ShoppingCart className="h-5 w-5 text-zinc-400" />
                      </div>
                      <div className="mt-4 text-sm font-semibold">
                        Aktif sepet yok
                      </div>
                      <div className="mt-1 text-xs text-zinc-500">
                        Ziyaretçiler sepete ürün eklediğinde burada listelenir.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                carts.map((c) => (
                  <CartRow
                    key={c._cart_id}
                    cart={c}
                    isSelected={selected.has(c._cart_id)}
                    onToggle={toggleOne}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating action bar */}
      {selected.size > 0 ? (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 fab-blur border border-zinc-300 shadow-lg rounded-sm px-4 py-3 flex items-center gap-4"
          data-testid="floating-action-bar"
        >
          <div className="text-sm">
            <span className="font-black tabular-nums font-mono">
              {selected.size}
            </span>{" "}
            sepet seçildi
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="rounded-sm h-9 text-zinc-600 hover:bg-zinc-100"
            onClick={() => setSelected(new Set())}
            data-testid="clear-selection-btn"
          >
            Temizle
          </Button>
          <Button
            size="sm"
            onClick={() => setConfirmOpen(true)}
            className="rounded-sm h-9 bg-[#002FA7] hover:bg-[#001F70] text-white transition-colors duration-150"
            data-testid="bulk-create-drafts-btn"
          >
            <FileText className="h-4 w-4 mr-1.5" />
            Taslak Siparişe Çevir
          </Button>
        </div>
      ) : null}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="confirm-dialog">
          <DialogHeader>
            <DialogTitle>Taslak siparişleri oluştur</DialogTitle>
            <DialogDescription>
              Seçilen{" "}
              <span className="font-mono font-black">{selected.size}</span>{" "}
              sepet için ayrı ayrı taslak sipariş oluşturulacak. Devam edilsin
              mi?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setConfirmOpen(false)}
              disabled={creating}
              data-testid="confirm-cancel-btn"
            >
              Vazgeç
            </Button>
            <Button
              className="rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white"
              onClick={doCreate}
              disabled={creating}
              data-testid="confirm-create-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Oluşturuluyor…
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-1.5" />
                  Onayla ve Oluştur
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={resultOpen} onOpenChange={setResultOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="results-dialog">
          <DialogHeader>
            <DialogTitle>Taslak siparişler oluşturuldu</DialogTitle>
            <DialogDescription>
              Her satır bir sepetin sonucudur. IdeaSoft paneline gitmek için bağlantıya tıklayın.
            </DialogDescription>
          </DialogHeader>
          <div className="border border-zinc-200 divide-y divide-zinc-200 max-h-[420px] overflow-y-auto">
            {results.map((r) => (
              <div
                key={r.cart_id}
                className="flex items-center gap-3 px-3 py-2 text-sm"
                data-testid={`result-row-${r.cart_id}`}
              >
                {r.ok ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 text-red-600 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">
                      Sepet #{r.cart_id}
                    </span>
                    {r.draft_order_name ? (
                      <Badge
                        variant="secondary"
                        className="rounded-sm bg-zinc-100 text-zinc-800 border border-zinc-200 font-mono text-[10px] uppercase tracking-wider"
                      >
                        {r.draft_order_name}
                      </Badge>
                    ) : null}
                  </div>
                  {r.ok ? (
                    r.draft_order_url ? (
                      <>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <a
                            href={r.draft_order_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[13px] text-[#002FA7] hover:underline break-all min-w-0"
                            data-testid={`result-link-${r.cart_id}`}
                            title={r.draft_order_url}
                          >
                            <ExternalLink className="h-3 w-3 shrink-0" />
                            <span className="truncate max-w-[300px]">{r.draft_order_url}</span>
                          </a>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(r.draft_order_url);
                                toast.success("Link kopyalandı");
                              } catch {
                                toast.error("Kopyalama başarısız");
                              }
                            }}
                            className="shrink-0 rounded-sm border border-zinc-200 hover:bg-zinc-50 p-1 text-zinc-600 hover:text-zinc-900"
                            data-testid={`result-copy-${r.cart_id}`}
                            title="Linki kopyala"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setQrOpen((prev) => ({
                                ...prev,
                                [r.cart_id]: !prev[r.cart_id],
                              }))
                            }
                            className={`shrink-0 rounded-sm border p-1 ${
                              qrOpen[r.cart_id]
                                ? "border-[#002FA7] bg-[#EEF2FF] text-[#002FA7]"
                                : "border-zinc-200 hover:bg-zinc-50 text-zinc-600 hover:text-zinc-900"
                            }`}
                            data-testid={`result-qr-btn-${r.cart_id}`}
                            title={qrOpen[r.cart_id] ? "QR'ı gizle" : "QR kodunu göster"}
                            aria-pressed={!!qrOpen[r.cart_id]}
                          >
                            <QrCode className="h-3 w-3" />
                          </button>
                        </div>
                        {qrOpen[r.cart_id] ? (
                          <div
                            className="mt-2 inline-block rounded-sm border border-zinc-200 bg-white p-2"
                            data-testid={`result-qr-${r.cart_id}`}
                          >
                            <QRCode
                              value={r.draft_order_url}
                              size={140}
                              bgColor="#FFFFFF"
                              fgColor="#0A0A0A"
                              level="M"
                              className="block"
                            />
                            <div className="mt-1 text-center text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                              Müşteriye telefonla okutun
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <div className="text-xs text-zinc-500 mt-0.5">
                        Bağlantı üretilemedi{r.draft_order_id ? ` · #${r.draft_order_id}` : ""}
                      </div>
                    )
                  ) : (
                    <div
                      className="text-xs text-red-700 mt-0.5 break-words"
                      data-testid={`result-error-${r.cart_id}`}
                    >
                      {r.error || "Bilinmeyen hata"}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={() => setResultOpen(false)}
              data-testid="results-close-btn"
            >
              Kapat
            </Button>
            <Link
              to="/history"
              onClick={() => setResultOpen(false)}
              className="inline-flex items-center gap-1.5 rounded-sm bg-[#002FA7] hover:bg-[#001F70] text-white px-4 h-9 text-sm font-medium"
              data-testid="results-history-btn"
            >
              <FileText className="h-4 w-4" />
              Geçmişi Aç
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
